import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  addIsoDays,
  mondayForIsoDate,
  toIsoDateLocal,
  workWeekForIsoDate,
} from "../../planning/planning-core.js";
import "../planning-enhancements.css";
import {
  planningApi,
  PlanningApiError,
  type PlanningProjectPlan,
  type PlanningWorkPackage,
} from "../lib/planningApi";
import {
  placementDateInWindow,
  placementWeekInHorizon,
  placeUnscheduledAtDate,
  type PlanningDateRange,
  shiftRangeToDay,
  shiftRangeToWeek,
} from "../lib/planningDnD";

const PROJECT_STORAGE_KEY = "dbvdw-planning-project";
type BoardMode = "day" | "week";
type TargetUnit = "day" | "week";

type QuickCreatePreset = {
  unit: TargetUnit;
  date: string;
};

function dayLabel(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function itemMeta(item: PlanningWorkPackage): string {
  return [item.type?.name, item.status?.name, item.assignee?.name]
    .filter(Boolean)
    .join(" · ");
}

function normalizeScheduledRange(
  item: PlanningWorkPackage,
): PlanningDateRange | null {
  const startDate = item.startDate ?? item.dueDate;
  const dueDate = item.dueDate ?? item.startDate;
  if (!startDate || !dueDate) return null;
  return { startDate, dueDate };
}

function rangeForTarget(
  item: PlanningWorkPackage,
  unit: TargetUnit,
  targetDate: string,
): PlanningDateRange {
  const normalized = normalizeScheduledRange(item);
  if (!normalized) {
    return placeUnscheduledAtDate(
      unit === "week" ? mondayForIsoDate(targetDate) : targetDate,
    );
  }
  const normalizedItem = { ...item, ...normalized };
  return unit === "week"
    ? shiftRangeToWeek(normalizedItem, targetDate)
    : shiftRangeToDay(normalizedItem, targetDate);
}

function ItemCard({
  item,
  canWrite,
  selected,
  busy,
  onSelect,
  onDragStart,
}: {
  item: PlanningWorkPackage;
  canWrite: boolean;
  selected: boolean;
  busy: boolean;
  onSelect: (id: number) => void;
  onDragStart: (id: number, event: React.DragEvent<HTMLElement>) => void;
}) {
  return (
    <article
      className={`planning-dnd-card${selected ? " planning-dnd-card--selected" : ""}`}
      draggable={canWrite && !busy}
      onDragStart={(event) => onDragStart(item.id, event)}
    >
      <div className="planning-dnd-card__heading">
        <strong>#{item.id} {item.subject}</strong>
        {!item.startDate && !item.dueDate && <span className="planning-badge">zonder datum</span>}
      </div>
      <div className="planning-card__meta">{itemMeta(item) || "—"}</div>
      {(item.startDate || item.dueDate) && (
        <div className="planning-card__meta">
          {item.startDate ?? "—"} → {item.dueDate ?? "—"}
        </div>
      )}
      {canWrite && (
        <button
          type="button"
          className="secondary planning-dnd-card__select"
          disabled={busy}
          aria-pressed={selected}
          onClick={() => onSelect(item.id)}
        >
          {selected ? "Gekozen" : "Kies voor inplannen"}
        </button>
      )}
    </article>
  );
}

function DropColumn({
  unit,
  date,
  label,
  items,
  canWrite,
  busy,
  selectedItemId,
  onSelect,
  onDropItem,
  onQuickCreate,
  onDragStart,
}: {
  unit: TargetUnit;
  date: string;
  label: string;
  items: PlanningWorkPackage[];
  canWrite: boolean;
  busy: boolean;
  selectedItemId: number | null;
  onSelect: (id: number) => void;
  onDropItem: (id: number, unit: TargetUnit, date: string) => void;
  onQuickCreate: (unit: TargetUnit, date: string) => void;
  onDragStart: (id: number, event: React.DragEvent<HTMLElement>) => void;
}) {
  return (
    <section
      className="planning-dnd-column"
      data-planning-drop-unit={unit}
      data-planning-drop-date={date}
      onDragOver={(event) => {
        if (!canWrite || busy) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }}
      onDrop={(event) => {
        if (!canWrite || busy) return;
        event.preventDefault();
        const parsed = Number(event.dataTransfer.getData("text/plain"));
        if (Number.isInteger(parsed) && parsed > 0) {
          onDropItem(
            parsed,
            unit,
            date,
          );
        }
      }}
    >
      <header className="planning-dnd-column__header">
        <div>
          <strong>{label}</strong>
          <span>{date}</span>
        </div>
        {canWrite && (
          <button
            type="button"
            className="secondary planning-dnd-quick-create"
            disabled={busy}
            onClick={() => onQuickCreate(unit, date)}
            aria-label={`Nieuw planningitem in ${label}`}
          >
            +
          </button>
        )}
      </header>
      <div className="planning-dnd-column__body">
        {items.length === 0 && <p className="planning-dnd-empty">Nog niets gepland.</p>}
        {items.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            canWrite={canWrite}
            selected={selectedItemId === item.id}
            busy={busy}
            onSelect={onSelect}
            onDragStart={onDragStart}
          />
        ))}
      </div>
      {canWrite && (
        <button
          type="button"
          className="secondary planning-dnd-drop-action"
          disabled={busy || !selectedItemId}
          onClick={() => selectedItemId && onDropItem(selectedItemId, unit, date)}
        >
          Plan gekozen item hier
        </button>
      )}
    </section>
  );
}

function QuickCreateDialog({
  preset,
  plan,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  preset: QuickCreatePreset;
  plan: PlanningProjectPlan;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (subject: string, typeId: number) => void;
}) {
  const [subject, setSubject] = useState("");
  const [typeId, setTypeId] = useState(Number(plan.types?.[0]?.id ?? 0));
  const label = preset.unit === "day"
    ? dayLabel(preset.date)
    : workWeekForIsoDate(preset.date).label;

  return (
    <div
      className="planning-dnd-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Snel planningitem toevoegen"
    >
      <div className="planning-dnd-modal__card">
        <div className="planning-dnd-modal__heading">
          <div>
            <h3>Snel toevoegen</h3>
            <p className="muted">{label}</p>
          </div>
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <label>
          <span>Omschrijving</span>
          <input
            autoFocus
            value={subject}
            maxLength={255}
            disabled={busy}
            onChange={(event) => setSubject(event.target.value)}
          />
        </label>
        <label>
          <span>Type</span>
          <select
            value={typeId}
            disabled={busy}
            onChange={(event) => setTypeId(Number(event.target.value))}
          >
            {(plan.types ?? []).map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </label>
        {error && <p className="planning-form__error" role="alert">{error}</p>}
        <div className="planning-form__actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onClose}
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={busy || !subject.trim() || !typeId}
            onClick={() => onSubmit(subject.trim(), typeId)}
          >
            Toevoegen
          </button>
        </div>
      </div>
    </div>
  );
}

export function PlanningDnDPanel() {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [plan, setPlan] = useState<PlanningProjectPlan | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [mode, setMode] = useState<BoardMode>("day");
  const [weekStart, setWeekStart] = useState(() => mondayForIsoDate(toIsoDateLocal()));
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [quickCreate, setQuickCreate] = useState<QuickCreatePreset | null>(
    null,
  );
  const [quickCreateError, setQuickCreateError] = useState<string | null>(null);

  useEffect(() => {
    const sync = () => {
      const panel = document.querySelector<HTMLElement>(".planning-panel");
      if (panel) setPortalTarget(panel);
      const select = document.querySelector<HTMLSelectElement>(
        ".planning-toolbar select",
      );
      const stored = (() => {
        try {
          return localStorage.getItem(PROJECT_STORAGE_KEY);
        } catch {
          return null;
        }
      })();
      const nextProjectId = select?.value || stored;
      if (nextProjectId) {
        setProjectId((current) => current === nextProjectId ? current : nextProjectId);
      }
    };
    const handleChange = (event: Event) => {
      const target = event.target;
      if (
        target instanceof HTMLSelectElement &&
        target.closest(".planning-toolbar")
      ) sync();
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("change", handleChange, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("change", handleChange, true);
    };
  }, []);

  const loadPlan = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [projects, nextPlan] = await Promise.all([
        planningApi.listProjects(),
        planningApi.getPlan(projectId),
      ]);
      setCanWrite(
        projects.find((project) => project.dbvdwProjectId === projectId)
          ?.canWrite === true,
      );
      setPlan(nextPlan);
      setSelectedItemId((selected) =>
        selected && nextPlan.workPackages.some((item) => item.id === selected) ? selected : null
      );
      setStatus(
        `Planningtooling bijgewerkt ${
          new Date().toLocaleTimeString("nl-NL", {
            hour: "2-digit",
            minute: "2-digit",
          })
        }`,
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Planningtooling kon niet worden geladen.",
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  const weekEnd = addIsoDays(weekStart, 6);
  const lookaheadEnd = addIsoDays(weekStart, 83);
  const dayTargets = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addIsoDays(weekStart, index)),
    [weekStart],
  );
  const weekTargets = useMemo(
    () =>
      Array.from(
        { length: 12 },
        (_, index) => addIsoDays(weekStart, index * 7),
      ),
    [weekStart],
  );
  const unscheduled = useMemo(
    () => (plan?.workPackages ?? []).filter((item) => !item.startDate && !item.dueDate),
    [plan],
  );

  const dayItems = useMemo(() => {
    const result = new Map<string, PlanningWorkPackage[]>();
    for (const date of dayTargets) result.set(date, []);
    for (const item of plan?.workPackages ?? []) {
      const date = placementDateInWindow(item, weekStart, weekEnd);
      if (date && result.has(date)) result.get(date)?.push(item);
    }
    return result;
  }, [plan, dayTargets, weekStart, weekEnd]);

  const weekItems = useMemo(() => {
    const result = new Map<string, PlanningWorkPackage[]>();
    for (const date of weekTargets) result.set(date, []);
    for (const item of plan?.workPackages ?? []) {
      const week = placementWeekInHorizon(item, weekStart, lookaheadEnd);
      if (week && result.has(week)) result.get(week)?.push(item);
    }
    return result;
  }, [plan, weekTargets, weekStart, lookaheadEnd]);

  const scheduleItem = useCallback(
    async (itemId: number, unit: TargetUnit, targetDate: string) => {
      if (!projectId || !canWrite || busy) return;
      setBusy(true);
      setError(null);
      setStatus("Planningwijziging controleren...");
      try {
        const freshPlan = await planningApi.getPlan(projectId);
        const freshItem = freshPlan.workPackages.find((item) => item.id === itemId);
        if (!freshItem) {
          throw new Error("Planningitem niet gevonden. Ververs de planning.");
        }
        const range = rangeForTarget(freshItem, unit, targetDate);
        await planningApi.updateSchedule(projectId, freshItem.id, {
          expectedLockVersion: freshItem.lockVersion,
          startDate: range.startDate,
          dueDate: range.dueDate,
        });
        setSelectedItemId(null);
        await loadPlan();
        setStatus("Planningwijziging bevestigd");
      } catch (cause) {
        if (
          cause instanceof PlanningApiError &&
          ["conflict", "unreachable", "invalid_response"].includes(cause.code)
        ) {
          await loadPlan().catch(() => undefined);
          setError(`Planningwijziging niet bevestigd: ${cause.message}`);
        } else {
          setError(
            cause instanceof Error ? cause.message : "Planningwijziging mislukt.",
          );
        }
        setStatus("Wijziging niet bevestigd");
      } finally {
        setBusy(false);
      }
    },
    [projectId, canWrite, busy, loadPlan],
  );

  const createQuickItem = useCallback(
    async (subject: string, typeId: number) => {
      if (!projectId || !quickCreate || busy) return;
      setBusy(true);
      setQuickCreateError(null);
      try {
        const startDate = quickCreate.unit === "week"
          ? mondayForIsoDate(quickCreate.date)
          : quickCreate.date;
        await planningApi.createWorkPackage(projectId, {
          subject,
          typeId,
          parentId: null,
          startDate,
          dueDate: quickCreate.unit === "day" ? startDate : null,
        });
        setQuickCreate(null);
        await loadPlan();
        setStatus("Nieuw planningitem bevestigd");
      } catch (cause) {
        setQuickCreateError(
          cause instanceof Error ? cause.message : "Planningitem toevoegen mislukt.",
        );
      } finally {
        setBusy(false);
      }
    },
    [projectId, quickCreate, busy, loadPlan],
  );

  if (!portalTarget) return null;

  return createPortal(
    <section
      className="planning-dnd-panel"
      aria-label="Planning inbox en snel inplannen"
    >
      <div className="planning-dnd-heading">
        <div>
          <span className="planning-eyebrow">React planningtooling</span>
          <h2>Inbox & snel inplannen</h2>
          <p className="muted">
            Sleep op desktop, of kies een item en gebruik “Plan gekozen item hier” op touch en
            toetsenbord.
          </p>
        </div>
        <div className="planning-dnd-heading__actions">
          <button
            type="button"
            className="secondary"
            disabled={loading || busy}
            onClick={() => void loadPlan()}
          >
            Verversen
          </button>
          <span className="muted" aria-live="polite">{status}</span>
        </div>
      </div>

      {error && <div className="planning-error" role="alert">{error}</div>}
      {loading && !plan && <p>Planningtooling laden...</p>}

      {plan && (
        <>
          <section className="planning-dnd-inbox" aria-label="Planning-inbox">
            <header className="planning-dnd-inbox__header">
              <div>
                <strong>Nog te plannen</strong>
                <span className="muted">
                  Volledig ongedateerde werkpakketten
                </span>
              </div>
              <span className="planning-dnd-count">{unscheduled.length}</span>
            </header>
            {unscheduled.length === 0
              ? <p className="planning-dnd-empty">De planning-inbox is leeg.</p>
              : (
                <div className="planning-dnd-inbox__rail">
                  {unscheduled.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      canWrite={canWrite}
                      selected={selectedItemId === item.id}
                      busy={busy}
                      onSelect={(id) => setSelectedItemId((current) => current === id ? null : id)}
                      onDragStart={(id, event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", String(id));
                        setSelectedItemId(id);
                      }}
                    />
                  ))}
                </div>
              )}
          </section>

          <div className="planning-dnd-toolbar">
            <div
              className="planning-tabs"
              role="group"
              aria-label="Snel inplannen weergave"
            >
              <button
                type="button"
                aria-pressed={mode === "day"}
                onClick={() => setMode("day")}
              >
                Dagen
              </button>
              <button
                type="button"
                aria-pressed={mode === "week"}
                onClick={() => setMode("week")}
              >
                12 weken
              </button>
            </div>
            <div className="planning-week-controls">
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => setWeekStart((date) => addIsoDays(date, -7))}
              >
                ←
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => setWeekStart(mondayForIsoDate(toIsoDateLocal()))}
              >
                Deze week
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => setWeekStart((date) => addIsoDays(date, 7))}
              >
                →
              </button>
            </div>
          </div>

          <div className={`planning-dnd-board planning-dnd-board--${mode}`}>
            {(mode === "day" ? dayTargets : weekTargets).map((date) => (
              <DropColumn
                key={`${mode}-${date}`}
                unit={mode === "day" ? "day" : "week"}
                date={date}
                label={mode === "day" ? dayLabel(date) : workWeekForIsoDate(date).label}
                items={(mode === "day" ? dayItems : weekItems).get(date) ?? []}
                canWrite={canWrite}
                busy={busy}
                selectedItemId={selectedItemId}
                onSelect={(id) => setSelectedItemId((current) => current === id ? null : id)}
                onDropItem={(id, unit, targetDate) => void scheduleItem(id, unit, targetDate)}
                onQuickCreate={(unit, targetDate) => {
                  setQuickCreateError(null);
                  setQuickCreate({ unit, date: targetDate });
                }}
                onDragStart={(id, event) => {
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", String(id));
                  setSelectedItemId(id);
                }}
              />
            ))}
          </div>
        </>
      )}

      {quickCreate && plan && (
        <QuickCreateDialog
          preset={quickCreate}
          plan={plan}
          busy={busy}
          error={quickCreateError}
          onClose={() => {
            setQuickCreate(null);
            setQuickCreateError(null);
          }}
          onSubmit={(subject, typeId) => void createQuickItem(subject, typeId)}
        />
      )}
    </section>,
    portalTarget,
  );
}
